import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { Shield, Eye, Lock, ArrowRight, PackageOpen, AlertTriangle, Code, CreditCard, UserCheck, Bug, ShieldAlert } from 'lucide-react';
import { Section } from '../components/Section';

const heroSlides = [
  {
    headline: (
      <>
        Stop sensitive data leaking into{' '}
        <span className="bg-gradient-to-r from-red-accent to-cyan-info bg-clip-text text-transparent">
          AI tools
        </span>
        .
      </>
    ),
    description:
      'Redbax inspects every prompt, paste, and file upload to ChatGPT, Copilot, Claude, and 40+ AI services — blocking PII, payment data, and confidential IP before it leaves the endpoint.',
    cta: { label: 'Register Interest', to: '/contact' },
    secondary: { label: 'Learn More', to: '/features' },
  },
  {
    headline: (
      <>
        Protect your supply chain from{' '}
        <span className="bg-gradient-to-r from-amber-warn to-red-accent bg-clip-text text-transparent">
          malware
        </span>
        .
      </>
    ),
    description:
      'Redbax scans every package install across your fleet against 200k+ known CVEs. Malicious dependencies are blocked before they reach a single developer machine.',
    cta: { label: 'See How It Works', to: '/features' },
    secondary: { label: 'Learn More', to: '/features' },
  },
];

const features = [
  {
    icon: Shield,
    title: 'Real-time interception',
    description:
      'Inspects prompts at the OS level before they reach AI services. No browser extension, and nothing routed through a cloud proxy — inspection happens on the device. Covers browsers, desktop apps and IDEs — not just the browser.',
  },
  {
    icon: Eye,
    title: 'Smart detection',
    description:
      'ML and regex-powered classifier spots PII, PCI, source code, credentials, and confidential documents.',
  },
  {
    icon: Lock,
    title: 'Policy-driven control',
    description:
      'Block, warn, or log per data category. Set rules by team, role, or sensitivity level. Deploy in minutes.',
  },
];

const stats = [
  { value: '40+', label: 'AI services monitored' },
  { value: '<5ms', label: 'inspection latency' },
  { value: '99.7%', label: 'detection accuracy' },
  { value: '2 min', label: 'average deploy time' },
];

const carouselItems = [
  {
    icon: Code,
    threat: 'A developer pastes proprietary API code into ChatGPT for debugging help.',
    response: 'Redbax detects source code patterns and blocks the prompt before it leaves the device.',
    category: 'Source Code Leak',
    color: 'text-cyan-info',
    bgColor: 'bg-cyan-info/10',
    borderColor: 'border-cyan-info/30',
  },
  {
    icon: CreditCard,
    threat: 'A finance team member asks Claude to summarise a spreadsheet with customer card numbers.',
    response: 'Redbax identifies PCI data in the prompt and blocks it before it reaches the AI service.',
    category: 'PCI Data Exposure',
    color: 'text-amber-warn',
    bgColor: 'bg-amber-warn/10',
    borderColor: 'border-amber-warn/30',
  },
  {
    icon: UserCheck,
    threat: 'An HR manager uploads CVs containing personal details to an AI screening tool.',
    response: 'Redbax flags PII — names, addresses, dates of birth — and warns before the upload proceeds.',
    category: 'PII Disclosure',
    color: 'text-green-ok',
    bgColor: 'bg-green-ok/10',
    borderColor: 'border-green-ok/30',
  },
  {
    icon: Bug,
    threat: 'A contractor installs an npm package with a known critical CVE across the dev team.',
    response: 'Redbax blocks the install fleet-wide and alerts the SOC team within seconds.',
    category: 'Supply Chain Attack',
    color: 'text-red-accent',
    bgColor: 'bg-red-subtle',
    borderColor: 'border-red-accent/30',
  },
];

function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (animating || index === current) return;
      setAnimating(true);
      setTimeout(() => {
        setCurrent(index);
        setAnimating(false);
      }, 500);
    },
    [animating, current]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const slide = heroSlides[current];

  return (
    <section className="relative overflow-hidden px-6 pb-24 pt-12 md:pb-32 md:pt-20">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[600px] w-[600px] -translate-x-1/2 rounded-full bg-red-accent/5 blur-3xl" />
      <div className="pointer-events-none absolute -top-20 left-1/3 h-[400px] w-[400px] -translate-x-1/2 rounded-full bg-cyan-info/5 blur-3xl" />

      <div className="relative mx-auto max-w-4xl text-center">
        <div className="min-h-[180px] md:min-h-[140px]">
          <h1
            key={`hero-h-${current}`}
            className={`text-5xl font-extrabold leading-[1.1] tracking-tight md:text-7xl ${animating ? 'hero-exit' : 'hero-enter'}`}
          >
            {slide.headline}
          </h1>
        </div>
        <div className="min-h-[80px] md:min-h-[60px]">
          <p
            key={`hero-p-${current}`}
            className={`mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-text-secondary md:text-xl ${animating ? 'hero-exit' : 'hero-enter'}`}
            style={{ animationDelay: '0.08s' }}
          >
            {slide.description}
          </p>
        </div>
        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link
            to={slide.cta.to}
            className="inline-flex items-center gap-2 rounded-lg bg-red-accent px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-red-hover"
          >
            {slide.cta.label}
            <ArrowRight size={18} />
          </Link>
          <Link
            to={slide.secondary.to}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-6 py-3 text-base font-semibold text-text-primary transition-colors hover:border-text-secondary hover:bg-surface"
          >
            {slide.secondary.label}
          </Link>
        </div>

        <div className="mt-10 flex items-center justify-center gap-2">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === current ? 'w-8 bg-red-accent' : 'w-2 bg-border hover:bg-text-secondary'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Carousel() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (animating || index === current) return;
      setAnimating(true);
      setTimeout(() => {
        setCurrent(index);
        setAnimating(false);
      }, 400);
    },
    [animating, current]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % carouselItems.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const item = carouselItems[current];
  const Icon = item.icon;

  return (
    <div className="mx-auto max-w-3xl">
      <div className="relative min-h-[220px] md:min-h-[180px]">
        <div
          key={current}
          className={`rounded-xl border ${item.borderColor} bg-surface p-8 ${animating ? 'carousel-exit' : 'carousel-enter'}`}
        >
          <div className="mb-4 flex items-center gap-3">
            <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${item.bgColor}`}>
              <Icon size={18} className={item.color} />
            </div>
            <span className={`text-xs font-semibold uppercase tracking-wider ${item.color}`}>
              {item.category}
            </span>
          </div>
          <div className="space-y-3">
            <p className="text-base leading-relaxed text-text-secondary">
              <span className="font-medium text-text-primary">Threat:</span> {item.threat}
            </p>
            <p className="text-base leading-relaxed text-text-secondary">
              <span className="font-medium text-text-primary">Response:</span> {item.response}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-2">
        {carouselItems.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === current ? 'w-8 bg-red-accent' : 'w-2 bg-border hover:bg-text-secondary'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export function Home() {
  return (
    <>
      {/* Hero */}
      <HeroCarousel />

      {/* Problem strip */}
      <section className="bg-red-subtle px-6 py-16 md:py-20">
        <div className="mx-auto max-w-7xl">
          <h2 className="mb-12 text-center text-2xl font-bold text-text-primary md:text-3xl">
            Your team uses AI every day. So does your data.
          </h2>
          <div className="grid gap-8 md:grid-cols-3">
            {[
              '43% of employees share sensitive work data with AI tools without employer knowledge — CybSafe / National Cybersecurity Alliance, 2025',
              '33% of employees admit inputting sensitive data into unapproved AI tools — Gartner, 2025',
              'Nearly half of employees admit inputting personal or non-public company data into GenAI tools — Cisco, 2025',
            ].map((text) => (
              <div
                key={text}
                className="rounded-lg border border-red-accent/20 bg-background/50 p-6 text-center"
              >
                <p className="text-base font-medium leading-relaxed text-text-primary">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Threat carousel */}
      <Section>
        <h2 className="mb-4 text-center text-3xl font-bold text-text-primary md:text-4xl">
          Threats we stop every day
        </h2>
        <p className="mx-auto mb-12 max-w-xl text-center text-text-secondary">
          Real scenarios. Real-time protection.
        </p>
        <Carousel />
      </Section>

      {/* Feature cards */}
      <Section>
        <div className="grid gap-6 md:grid-cols-3">
          {features.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="group rounded-xl border-l-2 border-red-accent bg-surface p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_30px_-10px_rgba(229,72,77,0.15)]"
            >
              <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-red-subtle">
                <Icon size={20} className="text-red-accent" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-text-primary">{title}</h3>
              <p className="text-sm leading-relaxed text-text-secondary">{description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Metrics strip */}
      <section className="border-y border-border bg-surface px-6 py-16">
        <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-4">
          {stats.map(({ value, label }) => (
            <div key={label} className="text-center">
              <div className="text-4xl font-extrabold text-text-primary md:text-5xl">{value}</div>
              <p className="mt-2 text-sm text-text-secondary">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Supply chain attack section */}
      <section className="relative overflow-hidden px-6 py-24 md:py-32">
        <div className="pointer-events-none absolute right-0 top-0 h-[500px] w-[500px] rounded-full bg-red-accent/5 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full bg-amber-warn/5 blur-3xl" />

        <div className="relative mx-auto max-w-7xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-warn/30 bg-amber-warn/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-amber-warn">
                <AlertTriangle size={14} />
                Supply Chain Defence
              </div>
              <h2 className="text-3xl font-bold leading-tight text-text-primary md:text-4xl">
                Your code dependencies are an{' '}
                <span className="text-red-accent">attack surface</span>.
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-text-secondary">
                Redbax monitors every package install across your fleet. Known CVEs, malicious
                packages, and risky dependencies are flagged and blocked before they reach
                production.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  'Real-time CVE scanning against the OSV database — 200k+ known vulnerabilities',
                  'Block packages by severity, ecosystem, or custom policy',
                  'Automatic alerts when new CVEs affect installed packages',
                  'Coverage across npm, PyPI, NuGet, Maven, Go, Cargo, and RubyGems',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-subtle">
                      <div className="h-1.5 w-1.5 rounded-full bg-red-accent" />
                    </div>
                    <span className="text-sm leading-relaxed text-text-secondary">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="rounded-xl border border-border bg-surface p-6" style={{ animation: 'pulse-glow 3s ease-in-out infinite' }}>
                <div className="mb-4 flex items-center gap-2 border-b border-border pb-4">
                  <PackageOpen size={18} className="text-red-accent" />
                  <span className="text-sm font-semibold text-text-primary">
                    Package Security Monitor
                  </span>
                </div>
                <div className="space-y-3">
                  {[
                    {
                      pkg: 'lodash@4.17.20',
                      eco: 'npm',
                      severity: 'CRITICAL',
                      cve: 'CVE-2021-23337',
                      color: 'text-red-accent',
                      bg: 'bg-red-subtle',
                    },
                    {
                      pkg: 'requests@2.25.0',
                      eco: 'PyPI',
                      severity: 'HIGH',
                      cve: 'CVE-2023-32681',
                      color: 'text-amber-warn',
                      bg: 'bg-amber-warn/10',
                    },
                    {
                      pkg: 'jackson-databind@2.9.8',
                      eco: 'Maven',
                      severity: 'CRITICAL',
                      cve: 'CVE-2019-14540',
                      color: 'text-red-accent',
                      bg: 'bg-red-subtle',
                    },
                    {
                      pkg: 'pillow@9.0.0',
                      eco: 'PyPI',
                      severity: 'HIGH',
                      cve: 'CVE-2023-44271',
                      color: 'text-amber-warn',
                      bg: 'bg-amber-warn/10',
                    },
                  ].map((v) => (
                    <div
                      key={v.cve}
                      className="flex items-center justify-between rounded-lg border border-border bg-background p-3"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-text-primary">
                            {v.pkg}
                          </span>
                          <span className="shrink-0 rounded bg-surface-raised px-1.5 py-0.5 text-[10px] text-text-secondary">
                            {v.eco}
                          </span>
                        </div>
                        <span className="text-xs text-text-secondary">{v.cve}</span>
                      </div>
                      <span
                        className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-bold ${v.bg} ${v.color}`}
                      >
                        {v.severity}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                  <span className="text-xs text-text-secondary">4 blocked this week</span>
                  <span className="text-xs font-medium text-red-accent">View all</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="bg-gradient-to-r from-red-subtle to-surface px-6 py-20 md:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-text-primary md:text-4xl">
            See Redbax in action.
          </h2>
          <p className="mt-4 text-lg text-text-secondary">
            Be the first to know when we launch.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-red-accent px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-red-hover"
          >
            Register Interest
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
